import React from 'react';
import { BookOpen, FileSignature, FileText, Lock } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { adminApi } from '../../../lib/adminAuth';
import ProposalsPanel from './ProposalsPanel';
import ContractsPanel from './ContractsPanel';
import LibraryServices from './LibraryServices';
import LibraryClauses from './LibraryClauses';
import LibrarySettings from './LibrarySettings';
import ExpiringBanner from './ExpiringBanner';
import RenewDialog from './RenewDialog';

function ScopeLibrary({ lib, reload }) {
  return (
    <div className="soft-card p-4 sm:p-5" data-testid="scope-library">
      <div className="mb-4">
        <h3 className="font-semibold text-[#1a1a2e]">Scope of Work & Terms Library</h3>
        <p className="text-xs text-slate-500">The building blocks for every proposal and contract. Changes apply to new documents; existing ones keep their own copy.</p>
        {!lib.can_edit && <p className="text-xs text-amber-700 mt-1 flex items-center gap-1" data-testid="library-readonly"><Lock className="w-3 h-3" /> Only the Super Admin can edit the library.</p>}
      </div>
      <Tabs defaultValue="services">
        <TabsList className="mb-4">
          <TabsTrigger value="services" data-testid="library-tab-services">Services ({lib.services.length})</TabsTrigger>
          <TabsTrigger value="clauses" data-testid="library-tab-clauses">Clauses ({lib.clauses.length})</TabsTrigger>
          <TabsTrigger value="settings" data-testid="library-tab-settings">Company & defaults</TabsTrigger>
        </TabsList>
        <TabsContent value="services"><LibraryServices services={lib.services} canEdit={lib.can_edit} reload={reload} /></TabsContent>
        <TabsContent value="clauses"><LibraryClauses sections={lib.sections} clauses={lib.clauses} canEdit={lib.can_edit} reload={reload} /></TabsContent>
        <TabsContent value="settings"><LibrarySettings settings={lib.settings} canEdit={lib.can_edit} reload={reload} /></TabsContent>
      </Tabs>
    </div>
  );
}

export default function AgreementsHub() {
  const [tab, setTab] = React.useState('proposals');
  const [lib, setLib] = React.useState(null);
  const [openContract, setOpenContract] = React.useState(null);
  const reload = React.useCallback(() => adminApi.get('/api/admin/scope-library').then((r) => setLib(r.data)).catch(() => {}), []);
  React.useEffect(() => { reload(); }, [reload]);
  const [renewing, setRenewing] = React.useState(null);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const goContract = (id, autoSend = false) => { setTab('contracts'); setOpenContract({ id, autoSend }); };
  const handled = React.useCallback(() => setOpenContract(null), []);
  const startRenew = async (row) => {
    try { const r = await adminApi.get(`/api/admin/contracts/${row._id}`); setRenewing(r.data); } catch { setRenewing(row); }
  };
  return (
    <Tabs value={tab} onValueChange={setTab} data-testid="agreements-hub">
      <ExpiringBanner onRenew={startRenew} onOpen={(id) => goContract(id)} refreshKey={refreshKey} />
      {renewing && <RenewDialog contract={renewing} onClose={() => setRenewing(null)}
        onRenewed={(id, mode) => { setRenewing(null); setRefreshKey((k) => k + 1); goContract(id, mode === 'send'); }} />}
      <TabsList className="mb-4">
        <TabsTrigger value="proposals" data-testid="agreements-tab-proposals"><FileText className="w-4 h-4 mr-1.5" /> Proposals</TabsTrigger>
        <TabsTrigger value="contracts" data-testid="agreements-tab-contracts"><FileSignature className="w-4 h-4 mr-1.5" /> Contracts</TabsTrigger>
        <TabsTrigger value="library" data-testid="agreements-tab-library"><BookOpen className="w-4 h-4 mr-1.5" /> Scope Library</TabsTrigger>
      </TabsList>
      <TabsContent value="proposals"><ProposalsPanel settings={lib?.settings} onOpenContract={goContract} canDelete={!!lib?.can_edit} /></TabsContent>
      <TabsContent value="contracts"><ContractsPanel settings={lib?.settings} openId={openContract} onOpenHandled={handled} canDelete={!!lib?.can_edit} onRenew={startRenew} refreshKey={refreshKey} /></TabsContent>
      <TabsContent value="library">{lib ? <ScopeLibrary lib={lib} reload={reload} /> : null}</TabsContent>
    </Tabs>
  );
}
